import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { defineMessages } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { Segment } from '../../services/analytics-services/segment';
import { UserPermissionService } from '../../services/user-permission-service';
import Global from '../../store/global-selectors';
import { useFedExNoticeLastDismissedDate } from '../hooks/use-notice-last-dismissed-date';
import { useWallet } from '../hooks/use-wallet';
import { WalletRegistrationOrUpdateButton } from './wallet-registration-or-update-button';
import { useIsAccountEmailVerifiedSellerSetting } from '../../hooks/use-seller-setting';
import { Providers } from '../../enums/providers';
import { useFeatureFlag } from '../../feature-flags/hooks/use-feature-flag';
import { FeatureFlagDeclarations } from '../../feature-flags/feature-flag-declarations';

import style from './style.module.less';

const messages = defineMessages({
  activate: {
    defaultMessage: 'Activate Now',
    id: 'Q2QF9Y',
    description: 'button text in popup, clicking will open the wallet update or registration modal to add fedex',
  },
  title: {
    defaultMessage: 'FedEx services now available with ShipStation Carriers.',
    id: 'oLWZuG',
    description: 'title in popup, informing user that FedEx services are now available',
  },
  content: {
    defaultMessage: 'Start shipping with FedEx services by activating them in your Carrier settings.',
    id: 'X/R8iD',
    description: 'content in popup, informing user that FedEx services are now available',
  },
  dismiss: {
    defaultMessage: 'No Thanks',
    id: 'Gz7bAs',
    description: 'button text in popup, clicking will dismiss the popup',
  },
});

const FEDEX_POPUP = 'FEDEX_POPUP';
const DISMISSAL_EVENT = 'FED_EX_NOTICE_DISMISSED';

const OpenWalletModalButton = ({ openModal, dismissNotice }) => {
  const handleClick = useCallback(() => {
    openModal();
    dismissNotice();
  }, [openModal, dismissNotice]);

  return (
    <Button className={style.button} onClick={handleClick} theme={THEME_CONSTANTS.primary}>
      <Text textType={TextType.Paragraph} value={messages.activate} />
    </Button>
  );
};

export const FedExWalletAvailabilityNotice = () => {
  const isFedexNoticeModal = useFeatureFlag(FeatureFlagDeclarations.enableWalletedFedexNotification);
  const isWalletTermsModalEnabled = useFeatureFlag(FeatureFlagDeclarations.enableWalletTermsModal);
  const isFeatureEnabled = !isWalletTermsModalEnabled && isFedexNoticeModal;

  const { newLabelProviderIds } = useWallet();
  const userRoles = useSelector(Global.selectUseruserRoles);
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const sellerFromUS = sellerHomeCountry === 'US';
  const isEmailVerified = useIsAccountEmailVerifiedSellerSetting();
  const isExpiredAccount = useSelector(Global.selectSellerIsSubscriptionExpired);
  const sellerIsActiveAndEmailVerified = isEmailVerified && !isExpiredAccount;
  const canConfigureWallet =
    UserPermissionService.userHasAdministrationRole(userRoles) ||
    UserPermissionService.userHasAccountManagementRole(userRoles) ||
    UserPermissionService.userHasConfigurationRole(userRoles);
  const { noticeLastDismissedDate, dismissNotice } = useFedExNoticeLastDismissedDate();
  // Track dismissal, wallet has it's own tracking for activation
  const dismissNoticeWithTracking = useCallback(() => {
    const dismissedAt = new Date();
    dismissNotice(dismissedAt);
    Segment.trackEvent(DISMISSAL_EVENT);
  }, [dismissNotice]);

  const shouldShow = useMemo(
    () =>
      sellerIsActiveAndEmailVerified &&
      sellerFromUS &&
      !noticeLastDismissedDate &&
      canConfigureWallet &&
      newLabelProviderIds?.includes(Providers.FedExWalleted.labelProviderId),
    [noticeLastDismissedDate, newLabelProviderIds, canConfigureWallet, sellerFromUS, sellerIsActiveAndEmailVerified],
  );

  return (
    shouldShow &&
    isFeatureEnabled && (
      <div className={style.popup}>
        <Text className={style.title} textType={TextType.H3} value={messages.title} />
        <Text textType={TextType.Paragraph} value={messages.content} />
        <div className={style.buttons}>
          <WalletRegistrationOrUpdateButton trackingId={FEDEX_POPUP}>
            {({ openModal }) => <OpenWalletModalButton dismissNotice={dismissNotice} openModal={openModal} />}
          </WalletRegistrationOrUpdateButton>
          <Button className={style.button} onClick={dismissNoticeWithTracking} theme={THEME_CONSTANTS.secondary}>
            <Text textType={TextType.Paragraph} value={messages.dismiss} />
          </Button>
        </div>
      </div>
    )
  );
};
